// render-articles-tree.ts — the "articles-tree" renderer: ONE scope's articles as a FILE TREE.
//
// ⭐ Articles are a file structure, not a list. A category is a folder, article_parent_id is the
// path, and the flat table (render-table) can only ever show the leaves. This renders the shape the
// data has actually had since the system shipped — ft_article_tree() built it and nothing drew it.
//
// TWO MOUNTS, ONE COMPONENT:
//   · my_articles.php — the management tree. Scope comes from the toolbar picker and CHANGES
//     without a page load, so the mount listens for `ft-articles-tree-reload`.
//   · article.php     — the reader's left-hand nav. Same tree, `data-ft-compact` on, the article
//     being read passed as data-ft-active-id so its branch opens and it highlights.
//
// ⚠ EXPANSION STATE IS PER SCOPE AND PER BROWSER (localStorage). A wiki's shape is stable and
// personal — collapsing "Session Notes" once should stay collapsed tomorrow — and it is pure
// convenience, so a private window that cannot store it just opens the default set instead.

import type { CatalogListConfig } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

interface ArticleTreeNode {
    id: string;
    name: string;
    excerpt: string;
    is_category: number;
    type: string;
    tags: string;
    private: number;
    read_url: string;
    edit_url: string;
    visibility_html: string;
    children: ArticleTreeNode[];
}

interface ArticleTreeResponse {
    ok?: boolean;
    error?: string;
    scope?: { kind: string; id: string; name: string };
    nodes?: ArticleTreeNode[];
}

function icon(id: string, w = 16, h = 16): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, false) : '';
}

function el(tag: string, className = '', text = ''): HTMLElement {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
}

// ── Collapsed-branch memory ───────────────────────────────────────────────────────────────────
// Stores the COLLAPSED ids, not the expanded ones: a brand-new folder should appear open (you just
// made it, you want to see inside), and storing "expanded" would default every new folder shut.
function collapsedKey(scopeKind: string, scopeId: string): string {
    return `ft_articles_tree_collapsed:${scopeKind}:${scopeId}`;
}

function readCollapsed(scopeKind: string, scopeId: string): Set<string> {
    try {
        const raw = window.localStorage.getItem(collapsedKey(scopeKind, scopeId));
        const parsed = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
        return new Set();   // private window / storage blocked — everything opens, which is fine
    }
}

function writeCollapsed(scopeKind: string, scopeId: string, set: Set<string>): void {
    try {
        window.localStorage.setItem(collapsedKey(scopeKind, scopeId), JSON.stringify([...set]));
    } catch {
        /* convenience only — never worth failing the render over */
    }
}

// ── Search ────────────────────────────────────────────────────────────────────────────────────
// A node survives if IT matches or any DESCENDANT does; an ancestor of a match must stay visible or
// the match is unreachable. Returns the set of ids to show, so the walk happens once, not per node.
function matchingIds(nodes: ArticleTreeNode[], keyword: string): Set<string> | null {
    if (!keyword) return null;   // null = no filtering at all, distinct from "nothing matched"

    const keep = new Set<string>();

    const walk = (node: ArticleTreeNode): boolean => {
        const haystack = `${node.name} ${node.excerpt} ${node.tags} ${node.type}`.toLowerCase();
        let hit = haystack.includes(keyword);
        for (const child of node.children) {
            if (walk(child)) hit = true;   // ⚠ NOT `hit ||= walk(child)` — that short-circuits and
        }                                  // would stop marking the rest of the subtree once one hit.
        if (hit) keep.add(node.id);
        return hit;
    };

    nodes.forEach(walk);
    return keep;
}

export function mountCatalogArticlesTree(root: HTMLElement, config: CatalogListConfig): void {

    const container = results(root);
    if (!container) return;

    const compact = root.getAttribute('data-ft-compact') === '1';
    // ⭐ READING MODE (article.php). Browsing a wiki, an open tree is the point; READING one article,
    // 40 open folders bury the thing you are looking at. With this flag every folder starts CLOSED
    // except the ones on the path to the active article, which are the only ones that give the
    // reader their bearings.
    //
    // ⚠ Deliberately NOT persisted. `collapsed` storage records what the user shut, which cannot
    // express "shut by default, opened just now" — and a peek inside a folder on a page you are
    // reading is not a preference worth remembering tomorrow. Session-only, in memory.
    const collapseOthers = root.getAttribute('data-ft-collapse-others') === '1';
    const openedThisVisit = new Set<string>();
    const activeId = (root.getAttribute('data-ft-active-id') || '').toUpperCase();
    const showVisibility = root.getAttribute('data-ft-show-visibility') === '1';
    const emptyText = root.getAttribute('data-ft-empty-text') || 'Nothing here yet.';

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;

    let loaded: ArticleTreeResponse | null = null;
    // ⚠ Request generation. Scope changes with a click, and a big world's tree can outrun a small
    // one — without this, switching A → B while A is still in flight paints A's tree under B's
    // label. Every load stamps its number and only the newest is allowed to render.
    let generation = 0;

    const build = (): void => {

        container.textContent = '';

        const scope = loaded?.scope;
        const nodes = loaded?.nodes || [];
        if (!scope) return;

        const collapsed = readCollapsed(scope.kind, scope.id);
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const keep = matchingIds(nodes, keyword);

        if (!nodes.length) {
            container.appendChild(el('div', 'ft-articles-tree-empty text-muted py-3', emptyText));
            return;
        }

        // ⚠ A node on the path to the article being READ opens regardless of stored state, and a
        // SEARCH opens everything it kept — a hit hidden inside a collapsed folder reads as no hit.
        const openPath = new Set<string>();
        if (activeId) {
            const mark = (node: ArticleTreeNode, trail: string[]): void => {
                const here = [...trail, node.id];
                if (node.id.toUpperCase() === activeId) here.forEach((id) => openPath.add(id));
                node.children.forEach((child) => mark(child, here));
            };
            nodes.forEach((node) => mark(node, []));
        }

        const buildNode = (node: ArticleTreeNode, depth: number): HTMLElement | null => {

            if (keep && !keep.has(node.id)) return null;

            const isFolder = node.is_category === 1 || node.children.length > 0;
            const isOpen = !!keyword
                || openPath.has(node.id)
                || (collapseOthers ? openedThisVisit.has(node.id) : !collapsed.has(node.id));
            const isActive = !!activeId && node.id.toUpperCase() === activeId;

            const li = el('li', 'ft-articles-tree-node');
            const row = el('div', `ft-articles-tree-row${isActive ? ' is-active' : ''}`);
            row.style.paddingLeft = `${depth * (compact ? 0.75 : 1.1)}rem`;

            // The twisty is a BUTTON, not a click on the row: clicking the name must navigate, and a
            // row that both opens a folder and follows a link is the classic file-explorer footgun.
            if (isFolder) {
                const twisty = el('button', 'ft-articles-tree-twisty') as HTMLButtonElement;
                twisty.type = 'button';
                twisty.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                twisty.setAttribute('aria-label', isOpen ? 'Collapse' : 'Expand');
                twisty.textContent = isOpen ? '▾' : '▸';
                twisty.addEventListener('click', () => {
                    if (collapseOthers) {
                        // Reading mode toggles the in-memory set and stores nothing — see above.
                        if (openedThisVisit.has(node.id)) { openedThisVisit.delete(node.id); } else { openedThisVisit.add(node.id); }
                        build();
                        return;
                    }
                    const now = readCollapsed(scope.kind, scope.id);
                    if (now.has(node.id)) { now.delete(node.id); } else { now.add(node.id); }
                    writeCollapsed(scope.kind, scope.id, now);
                    build();
                });
                row.appendChild(twisty);
            } else {
                row.appendChild(el('span', 'ft-articles-tree-twisty is-leaf'));
            }

            const glyph = el('span', 'ft-articles-tree-glyph');
            glyph.innerHTML = icon(node.is_category === 1 ? 'category' : 'article', compact ? 15 : 18, compact ? 15 : 18);
            row.appendChild(glyph);

            const link = el('a', 'ft-articles-tree-name') as HTMLAnchorElement;
            link.href = node.read_url;
            link.textContent = node.name;
            row.appendChild(link);

            if (!compact && node.type) {
                row.appendChild(el('span', 'ft-articles-tree-type badge', node.type));
            }
            if (!compact && node.excerpt) {
                row.appendChild(el('span', 'ft-articles-tree-excerpt text-muted small', node.excerpt));
            }

            if (!compact) {
                const actions = el('div', 'ft-articles-tree-actions ms-auto d-flex align-items-center gap-2');
                if (showVisibility && node.visibility_html) {
                    // Server-rendered so the SAME button (and the same delegated click handler) serves
                    // the tree and the flat list. It carries icon_ph placeholders — initTooltips()
                    // below runs ftMountIcons over the container, which hydrates them.
                    const vis = el('span', 'ft-articles-tree-vis');
                    vis.innerHTML = node.visibility_html;
                    actions.appendChild(vis);
                }
                const edit = el('a', 'btn btn-sm ft-kin-btn ft-articles-tree-edit') as HTMLAnchorElement;
                edit.href = node.edit_url;
                edit.innerHTML = icon('edit', 15, 15);
                edit.setAttribute('data-bs-toggle', 'tooltip');
                edit.setAttribute('data-bs-original-title', 'Edit this article');
                actions.appendChild(edit);
                row.appendChild(actions);
            }

            li.appendChild(row);

            if (node.children.length && isOpen) {
                const kids = el('ul', 'ft-articles-tree-children');
                node.children.forEach((child) => {
                    const built = buildNode(child, depth + 1);
                    if (built) kids.appendChild(built);
                });
                if (kids.childElementCount) li.appendChild(kids);
            }

            return li;
        };

        const list = el('ul', `ft-articles-tree${compact ? ' is-compact' : ''}`);
        nodes.forEach((node) => {
            const built = buildNode(node, 0);
            if (built) list.appendChild(built);
        });

        if (!list.childElementCount) {
            container.appendChild(el('div', 'ft-articles-tree-empty text-muted py-3', 'Nothing matches that search.'));
            return;
        }

        container.appendChild(list);
        initTooltips(container);
    };

    const load = async (): Promise<void> => {

        const mine = ++generation;
        const scopeKind = root.getAttribute('data-ft-scope-kind') || '';
        const scopeId = root.getAttribute('data-ft-scope-id') || '';

        // No scope chosen yet is the NORMAL first-visit state on my_articles, not a failure. The page
        // owns that empty state (it has the picker); the tree just stays quiet.
        if (!scopeKind || !scopeId) {
            loaded = null;
            container.textContent = '';
            hideLoader(root);
            setStatus(root, '');
            return;
        }

        showLoader(root, config);
        try {
            const params = new URLSearchParams({ scope_kind: scopeKind, scope_id: scopeId });
            const res = await fetch(`${resolveUrl(config.endpoint)}?${params.toString()}`, { credentials: 'same-origin' });
            const data: ArticleTreeResponse = await res.json().catch(() => ({} as ArticleTreeResponse));
            if (mine !== generation) return;   // a newer scope is already loading — this answer is stale
            if (!res.ok || data.ok !== true) {
                throw new Error(data.error || `HTTP ${res.status}`);
            }
            loaded = data;
            hideLoader(root);
            setStatus(root, '');
            build();
        } catch (e) {
            if (mine !== generation) return;   // ...including a stale FAILURE, which would otherwise
                                               // replace the current tree with an error message
            loaded = null;
            container.textContent = '';
            hideLoader(root);
            console.error('[articles-tree] fetch/render failed', e);
            setStatus(root, e instanceof Error ? e.message : 'Could not load these articles.');
        }
    };

    // The scope changes without a page load, so the page tells the mount to refetch. Also used after
    // a create/delete elsewhere on the page.
    root.addEventListener('ft-articles-tree-reload', () => { void load(); });

    // Search re-filters the ALREADY-fetched tree — no refetch. A wiki is small enough to hold, and a
    // request per keystroke would make the tree feel worse than the table it replaced.
    if (searchInput) {
        searchInput.addEventListener('input', () => { if (loaded) build(); });
    }

    void load();
}
